import React, { ReactElement } from 'react';
import { Control, Controller, FormProvider, useForm } from 'react-hook-form';
import { usePostHog } from '@posthog/react';
import { LazyMotion } from 'motion/react';

import SwitchInput from 'components/forms/inputs/rhf-inputs/SwitchInput';
import { MonitorIcon, MoonIcon, SunIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader';
import { Switch } from 'components/ui/switch';
import { announceDesignSwitch, useUiDesign } from 'hooks/useUiDesign';
import { Segmented } from 'pages/home/widgets/creator/Segmented';
import { useGetUserDataQuery, useUpdateCurrentUserPreferencesMutation } from 'store/api/user';
import { UiTheme, UpdateUserPreferences } from 'types/user';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { loadMotionFeatures } from 'utils/motion';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

type UserPreferencesModalProps = {
    onClose: () => void;
};

// The switch shows the effective design and `theme` the effective theme, Light when unset, while the stored
// `uiDesign` and `newDesignTheme` may be unset: each is written only when its control moves, since saving the default
// for someone who never chose would pin them to it.
type PreferencesForm = UpdateUserPreferences & { useNewDesign: boolean; theme: UiTheme };

const THEMES = [
    { value: 'LIGHT', label: 'user.preferences.appearance-light', Icon: SunIcon },
    { value: 'SYSTEM', label: 'user.preferences.appearance-system', Icon: MonitorIcon },
    { value: 'DARK', label: 'user.preferences.appearance-dark', Icon: MoonIcon },
] as const;

type PreferenceSwitchProps = {
    name: 'highlightRequiredFields' | 'newDesignHighlightRequiredFields' | 'useNewDesign';
    control: Control<PreferencesForm>;
    label: string;
    tooltip?: ReactElement;
    isNew: boolean;
};

/**
 * One preference. The new design draws the row itself: the label, with its help, on the dialog's leading edge
 * and the switch on its trailing one, so the switches share an edge. Classic keeps `SwitchInput`'s row, each
 * switch after its own label.
 */
function PreferenceSwitch({ name, control, label, tooltip, isNew }: PreferenceSwitchProps) {
    if (!isNew) {
        return <SwitchInput name={name} control={control} label={label} tooltip={tooltip} />;
    }
    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { ref, value, onBlur, onChange } }) => (
                <div className="flex items-center justify-between gap-1.5">
                    <span className="flex items-center gap-1.5">
                        <label htmlFor={name} className="text-fg-secondary text-sm">
                            {t(label)}
                        </label>
                        {tooltip}
                    </span>
                    <Switch
                        id={name}
                        name={name}
                        ref={ref}
                        className="m-2"
                        onBlur={onBlur}
                        checked={!!value}
                        onCheckedChange={onChange}
                    />
                </div>
            )}
        />
    );
}

const UserPreferencesModal: React.FC<UserPreferencesModalProps> = ({ onClose }) => {
    const { data: userData, isLoading } = useGetUserDataQuery();
    const [updatePreferences, { isLoading: isUpdating }] = useUpdateCurrentUserPreferencesMutation();
    const { isNew, canToggle, isImpersonating } = useUiDesign();
    const posthog = usePostHog();
    // This form saves the preferences of whoever the session acts as, so the design and the theme are offered to
    // oneself only.
    const canSaveDesign = canToggle && !isImpersonating;

    const storedTheme = userData?.preferences.newDesignTheme ?? 'LIGHT';
    const preferences = userData && { ...userData.preferences, useNewDesign: isNew, theme: storedTheme };
    const formMethods = useForm<PreferencesForm>({
        values: preferences,
        defaultValues: preferences,
    });

    const { control, handleSubmit } = formMethods;

    const onSubmit = async ({ useNewDesign, theme, ...data }: PreferencesForm) => {
        const switched = canSaveDesign && useNewDesign !== isNew;
        const design = useNewDesign ? 'NEW' : 'CLASSIC';
        try {
            await updatePreferences({
                ...data,
                ...(switched && { uiDesign: design }),
                ...(theme !== storedTheme && { newDesignTheme: theme }),
            }).unwrap();
            if (switched) {
                announceDesignSwitch(posthog, design, 'preferences');
            }
            onClose();
        } catch {
            // The API's error handler has toasted the failure; classic adds its own on top.
            if (!isNew) {
                errorAlert(t('forms.error-submitting-the-form'));
            }
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <FormProvider {...formMethods}>
            <form
                onSubmit={(e) => {
                    void handleSubmit(onSubmit)(e);
                }}
            >
                <ModalHeader title={t('user.preferences-title')} onCancel={onClose} />
                <div className="overflow-y-auto p-6 max-sm:px-4">
                    <div className="flex flex-col gap-4">
                        {isNew && !isImpersonating && (
                            // Wraps on a phone, where the label and the three segments do not fit one line.
                            <div className="flex flex-wrap items-center justify-between gap-1.5">
                                <span id="appearance-label" className="text-fg-secondary text-sm">
                                    {t('user.preferences.appearance')}
                                </span>
                                <Controller
                                    name="theme"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        // Its pill slides with Motion, whose features the layouts' `LazyMotion`
                                        // does not reach up here. `me-2` lines its trailing edge up with the
                                        // switches' `m-2`.
                                        <LazyMotion features={loadMotionFeatures} strict>
                                            <Segmented
                                                labelledBy="appearance-label"
                                                value={value}
                                                onChange={onChange}
                                                className="me-2"
                                                options={THEMES.map(({ value: theme, label, Icon }) => ({
                                                    value: theme,
                                                    label: (
                                                        <>
                                                            <Icon />
                                                            {t(label)}
                                                        </>
                                                    ),
                                                }))}
                                            />
                                        </LazyMotion>
                                    )}
                                />
                            </div>
                        )}
                        {/* Each design has its own choice. The key follows the design shown, never the form's
                            design switch, so moving both in one save edits the choice of the design on screen. */}
                        <PreferenceSwitch
                            name={isNew ? 'newDesignHighlightRequiredFields' : 'highlightRequiredFields'}
                            control={control}
                            label="user.preferences.highlight-required-fields"
                            isNew={isNew}
                        />
                        {canSaveDesign && (
                            <PreferenceSwitch
                                name="useNewDesign"
                                control={control}
                                label="design.use-new"
                                tooltip={<InfoTooltip text="design.use-new-help" />}
                                isNew={isNew}
                            />
                        )}
                    </div>
                </div>
                <ModalFooter
                    onCancel={onClose}
                    onSave={() => {
                        void handleSubmit(onSubmit)();
                    }}
                    saveButtonTextId="save"
                    cancelButtonTextId="cancel"
                    disableSave={isUpdating}
                />
            </form>
        </FormProvider>
    );
};

export default UserPreferencesModal;
